import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { useI18n } from "@/lib/i18n";
import { LogOut, User as UserIcon, Plane, ShoppingBag, LayoutDashboard, Package, Sun, Moon, Languages, Wallet } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import BottomNav from "@/components/BottomNav";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import SwitchModeToggle from "@/components/SwitchModeToggle";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useState, useEffect } from "react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname.startsWith(path);
  const isAdmin = user?.role === "admin";
  const activeMode = user?.active_mode || user?.role || "buyer";

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    if (user && "Notification" in window && Notification.permission === "default") {
      const t = setTimeout(() => Notification.requestPermission().catch(() => {}), 4000);
      return () => clearTimeout(t);
    }
  }, [user]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top)", backgroundColor: "var(--app-bg)", color: "var(--app-fg)" }}
    >
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink min-w-0" data-testid="logo-link">
            <img
              src="/icons/icon-192.png"
              alt="Jastip-Safe"
              className="w-9 h-9 rounded-lg shadow-sm flex-shrink-0"
              data-testid="navbar-logo-img"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-semibold text-base sm:text-lg tracking-tight">Jastip-Safe</span>
              <span className="text-[10px] font-mono text-zinc-500 -mt-0.5">Personal Shopping · Escrow</span>
            </div>
          </Link>

          {user && !isAdmin ? (
            <nav className="hidden md:flex items-center gap-1">
              <NavLink to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboard")} active={isActive("/dashboard")} testid="nav-dashboard" />
              {/* Iter 16: Buyer mode → show "Find Trips" + "My Requests".
                  Jastiper mode → hide these (jastiper uses Catalog as primary entry). */}
              {activeMode === "buyer" && (
                <>
                  <NavLink to="/trips" icon={Plane} label={t("nav.find_trips")} active={isActive("/trips")} testid="nav-trips" />
                  <NavLink to="/requests" icon={ShoppingBag} label={t("nav.my_requests")} active={isActive("/requests")} testid="nav-requests" />
                </>
              )}
              {activeMode === "jastiper" && (
                <>
                  <NavLink to="/catalog" icon={Package} label={t("nav.catalog")} active={isActive("/catalog")} testid="nav-catalog" />
                  <NavLink to="/finance" icon={Wallet} label="Finance" active={isActive("/finance")} testid="nav-finance" />
                </>
              )}
            </nav>
          ) : isAdmin ? (
            <nav className="hidden md:flex items-center gap-1">
              <NavLink to="/admin/dashboard" icon={LayoutDashboard} label={t("nav.admin")} active={isActive("/admin")} testid="nav-admin-dashboard" />
            </nav>
          ) : null}

          <div className="flex items-center gap-1 sm:gap-1.5">
            {/* Language toggle — desktop only (also available in /profile) */}
            <button
              onClick={() => setLang(lang === "id" ? "en" : "id")}
              className="hidden md:flex w-9 h-9 border border-[var(--app-border)] bg-[var(--app-surface)] hover:bg-[var(--app-surface-alt)] items-center justify-center rounded-md transition-colors"
              title={t("common.language")}
              data-testid="lang-toggle-btn"
            >
              <span className="text-xs font-mono font-semibold uppercase">{lang}</span>
            </button>
            {/* Theme toggle — desktop only (also available in /profile) */}
            <button
              onClick={toggleTheme}
              className="hidden md:flex w-9 h-9 border border-[var(--app-border)] bg-[var(--app-surface)] hover:bg-[var(--app-surface-alt)] items-center justify-center rounded-md transition-colors"
              title={theme === "dark" ? t("common.theme_light") : t("common.theme_dark")}
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? <Sun size={14} strokeWidth={1.8} /> : <Moon size={14} strokeWidth={1.8} />}
            </button>

            {user ? (
              <>
                {!isAdmin && <NotificationBell />}
                {!isAdmin ? (
                  <div
                    className="flex items-stretch border border-[var(--app-border)] bg-[var(--app-surface)] rounded-md overflow-hidden"
                    data-testid="user-mode-group"
                  >
                    <Link
                      to="/profile"
                      className="flex items-center gap-1.5 px-2 sm:px-3 py-2 hover:bg-[var(--app-surface-alt)] transition-colors"
                      title="Profil & Pengaturan"
                      data-testid="user-pill-link"
                    >
                      <UserIcon size={14} strokeWidth={1.5} className="flex-shrink-0" />
                      <span className="text-sm font-medium max-w-[80px] sm:max-w-[120px] truncate inline-flex items-center gap-1" data-testid="user-name">
                        <span className="hidden sm:inline">{user.name}</span>
                        <span className="inline sm:hidden">{(user.name || "").split(" ")[0]}</span>
                        {user.verified_badge && <VerifiedBadge size={12} testid="header-user-verified" />}
                      </span>
                    </Link>
                    {/* Iter 17: SwitchMode pill RIGHT NEXT TO the name, visually attached
                        so it's the most obvious thumb target on mobile. */}
                    <SwitchModeToggle attached />
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2 sm:px-3 py-2 border border-[var(--app-border)] bg-[var(--app-surface)] rounded-md">
                    <UserIcon size={14} strokeWidth={1.5} />
                    <span className="text-sm font-medium max-w-[80px] sm:max-w-[120px] truncate" data-testid="user-name">{user.name}</span>
                  </div>
                )}
                <button onClick={handleLogout} className="btn-secondary-flat !py-2 !px-2 sm:!px-3 text-sm" data-testid="logout-btn" title={t("nav.sign_out")}>
                  <LogOut size={14} strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <Link to="/auth" className="btn-primary-flat text-sm !py-2 !px-3 sm:!px-4" data-testid="login-link">
                {t("nav.sign_in")}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-24 md:pb-8">{children}</main>

      <footer className="hidden md:block border-t border-[var(--app-border)] mt-12 py-6 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="label-caps">© 2026 Jastip-Safe</div>
          <div className="flex items-center gap-6">
            <div className="text-xs font-mono" style={{color: "var(--app-muted)"}}>{lang === "id" ? "Escrow Aman · Kepercayaan Nyata" : "Secure Escrow · Real Trust"}</div>
          </div>
        </div>
      </footer>

      {user && !isAdmin && <BottomNav />}
      {user && <PWAInstallPrompt />}
    </div>
  );
}

function NavLink({ to, icon: Icon, label, active, testid }) {
  return (
    <Link
      to={to}
      className={`px-3.5 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
        active
          ? "bg-[var(--app-brand-soft)] text-[var(--app-brand)]"
          : "hover:bg-[var(--app-surface-alt)]"
      }`}
      data-testid={testid}
    >
      <Icon size={15} strokeWidth={1.6} /> {label}
    </Link>
  );
}
